import React from 'react';
import { Box, Button } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Fade from '@mui/material/Fade';
import { uniq } from 'lodash';
import { useAllTeams } from '../../hooks/useAllTeams';
import { useUserAuth } from '../../contexts/auth';
import TeamSelector from './team-selector';
import { useAppTeamConfigs } from '../../hooks/useGetAppConfigs';
import { supportedLeagues } from './utils';
import getLeagueName from '../../utils/getLeagueName';
import { SxStyle } from '../../types';

const styles: SxStyle = {
  container: {
    background: '#F1F5F9',
    padding: 1,
    borderRadius: '6px',
    position: 'absolute',
    width: '100%',
    zIndex: 1,
  },
  logo: {
    display: 'flex',
    justifyContent: 'center',
    mb: 5,
  },
  button: (theme) => ({
    justifyContent: 'flex-start',
    backgroundColor: theme.palette.menu.item + '7F', // 50% opacity
    color: '#FFF',
    borderRadius: '6px',
    border: 0,
    fontSize: '14px',
    lineHeight: '14px',
    textTransform: 'none',
    padding: '8px 14px',
  }),
};

interface Props {
  open: boolean;
  setOpen: (open: boolean) => void;
  onSelect: (selectedAbbrv: string) => void;
  selectedAbbrv: string;
}

export default function AppSelector(props: Props) {
  const { open, setOpen, onSelect, selectedAbbrv } = props;
  const { isSuperAdmin } = useUserAuth();
  const { data: teams = [] } = useAllTeams();
  const { data: teamsConfig = [] } = useAppTeamConfigs();

  const availableLeagues = uniq(teamsConfig.map((teamConfig) => teamConfig.data.configLeagueName));

  const tabs = supportedLeagues.filter((league) => {
    return availableLeagues.includes(league);
  });

  const isSelectedTeamAbbrv = !(supportedLeagues as string[]).includes(selectedAbbrv.toLowerCase());

  const [selectedLeague, setSelectedLeague] = React.useState<string | undefined>();
  const selectedTeam = isSelectedTeamAbbrv
    ? teams.find((team) => team.abbrv === selectedAbbrv)
    : undefined;
  const selectedLeagueAbbrv = supportedLeagues.find(
    (l) => l?.toLowerCase() === selectedAbbrv.toLowerCase()
  );

  React.useEffect(() => {
    const [firstTab] = tabs;
    if (!selectedAbbrv && firstTab) {
      const [firstTeam] = teamsConfig.filter(
        (teamsConfig) => teamsConfig.data.configLeagueName === firstTab
      );
      firstTeam && onSelect(firstTeam.abbrv);
      setSelectedLeague(firstTab);
    } else if (selectedTeam) {
      const league = teamsConfig.find((teamsConfig) => teamsConfig.abbrv === selectedAbbrv)?.data
        .configLeagueName;
      if (selectedLeague !== league && !open) {
        setSelectedLeague(league);
      }
    } else if (!selectedTeam && !selectedLeague && !isSelectedTeamAbbrv) {
      setSelectedLeague(selectedAbbrv);
    }
  }, [
    teamsConfig,
    tabs,
    selectedLeague,
    selectedAbbrv,
    selectedTeam,
    isSelectedTeamAbbrv,
    onSelect,
    open,
  ]);

  const selectedTeamLeague = React.useMemo(() => {
    if (selectedTeam) {
      const league = teamsConfig.find((teamsConfig) => teamsConfig.abbrv === selectedAbbrv)?.data
        .configLeagueName;
      return league || null;
    }

    return null;
  }, [selectedAbbrv, selectedTeam, teamsConfig]);

  return (
    <Box>
      <Box sx={styles.logo}>
        {selectedTeam?.logoImageUrl ? <img src={selectedTeam.logoImageUrl} height={80} /> : null}
        {!selectedTeam && selectedLeagueAbbrv && isSuperAdmin ? (
          <img src={`/leagues/${selectedLeagueAbbrv}.png`} height={80} />
        ) : null}
      </Box>
      <Button
        variant="outlined"
        fullWidth
        endIcon={open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        sx={styles.button}
        onClick={() => {
          if (teamsConfig.length > 1) {
            setOpen(!open);
          }
        }}
      >
        <Box sx={{ flex: '1 1 auto', display: 'flex', gap: 1, alignItems: 'center' }}>
          {selectedTeam ? (
            <>
              {selectedTeamLeague ? (
                <img src={`/leagues/${selectedTeamLeague.toLowerCase()}.png`} height={16} />
              ) : null}
              {selectedTeam.name}
            </>
          ) : null}
          {!selectedTeam && selectedLeagueAbbrv && isSuperAdmin ? (
            <>
              <img src={`/leagues/${selectedLeagueAbbrv}.png`} height={16} />
              {getLeagueName(selectedLeagueAbbrv)}
            </>
          ) : null}
        </Box>
      </Button>

      <Fade in={open}>
        <Box sx={styles.container}>
          <TeamSelector
            {...props}
            setOpen={setOpen}
            selectedLeague={selectedLeague?.toLowerCase()}
            setSelectedLeague={setSelectedLeague}
          />
        </Box>
      </Fade>
    </Box>
  );
}
